import React from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";

/**
 * Route-level error boundary. Catches any render / hook / import error thrown by a
 * child component and shows a friendly recovery card instead of a blank white screen.
 * Also silently reports the crash to /api/analytics/event so we can spot regressions
 * in production without user complaints.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: error?.message || String(error) };
  }

  componentDidCatch(error, info) {
    // Log to console for local debugging.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
    // Fire-and-forget analytics ping — never throw here.
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/analytics/event`;
      const body = JSON.stringify({
        event_type: "client_error",
        path: typeof window !== "undefined" ? window.location.pathname : "",
        metadata: {
          message: error?.message || String(error),
          stack: (error?.stack || "").slice(0, 2000),
          component_stack: (info?.componentStack || "").slice(0, 2000),
          user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        },
      });
      if (typeof navigator !== "undefined" && navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
      } else if (typeof fetch !== "undefined") {
        fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, credentials: "include" }).catch(() => {});
      }
    } catch { /* never let the boundary itself throw */ }
  }

  handleRefresh = () => {
    // Full reload — clears the tripped boundary state and re-fetches the JS bundle.
    if (typeof window !== "undefined") window.location.reload();
  };

  handleHome = () => {
    if (typeof window !== "undefined") window.location.assign("/");
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4 py-16" data-testid="error-boundary">
        <div className="max-w-lg w-full bg-white nh-brutal p-8 text-center">
          <div className="mx-auto w-14 h-14 border-2 border-black bg-[hsl(var(--nh-yellow))] flex items-center justify-center mb-4">
            <AlertTriangle className="w-7 h-7" strokeWidth={2.4} />
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter">Something went wrong.</h1>
          <p className="text-sm text-black/70 mt-2">
            This page hit an unexpected error. Refreshing usually fixes it. If the problem persists,
            head home and try again — we've quietly logged the error so we can look into it.
          </p>
          {this.state.message && (
            <p className="mt-3 text-[11px] font-mono text-black/40 truncate" title={this.state.message}>
              {this.state.message}
            </p>
          )}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={this.handleRefresh}
              data-testid="error-refresh-btn"
              className="inline-flex items-center gap-2 bg-[hsl(var(--nh-red))] hover:bg-[hsl(var(--nh-red-dark))] text-white rounded-none border-2 border-black nh-hover-push h-10 px-4 text-sm font-black uppercase tracking-widest"
            >
              <RefreshCw className="w-4 h-4" /> Refresh page
            </button>
            <button
              onClick={this.handleHome}
              data-testid="error-home-btn"
              className="inline-flex items-center gap-2 bg-white text-black rounded-none border-2 border-black nh-hover-push h-10 px-4 text-sm font-black uppercase tracking-widest"
            >
              <Home className="w-4 h-4" /> Go home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
